// ✅ Importaciones principales
import { useState } from 'react';
import { RegisterFormProps } from '@/types'; // Tipado de props que recibe el componente
import { ButtonSiguiente } from '../../ui'; // Botón personalizado reutilizable
import { isValidEmail } from '../../validation/validations'; // Función de validación de email
import { useNavigate } from 'react-router-dom'; // Hook para navegación programática
import { safeFetch } from '@/api/safeFetch';
import { Eye, EyeOff } from 'lucide-react';
// 🔹 Componente funcional que recibe una prop opcional onSuccess (acción al registrarse con éxito)
const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess }) => {

  // 🧱 Estados para controlar los campos y mensajes
  const [email, setEmail] = useState('');                  // Correo electrónico
  const [usuario, setUsuario] = useState('');              // Nombre de usuario
  const [contrasena, setContrasena] = useState('');        // Contraseña
  const [confirmarContrasena, setConfirmarContrasena] = useState(''); // Confirmación de contraseña
  const [error, setError] = useState('');                  // Mensaje de error
  const [mensaje, setMensaje] = useState('');              // Mensaje de éxito o información
  const [loading, setLoading] = useState(false);           // Estado de carga (para el botón)
  const navigate = useNavigate();                          // Hook para redirección
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);



  // 🔸 Maneja el envío del formulario de registro
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Previene que la página se recargue por defecto

    // Limpiamos mensajes anteriores
    setError('');
    setMensaje('');

    // 🔹 Validaciones básicas antes de enviar al backend
    if (!email || !usuario || !contrasena || !confirmarContrasena) {
      setError('Todos los campos son obligatorios');
      return;
    }

    // 🔹 Validar formato del correo electrónico
    if (!isValidEmail(email)) {
      setError('Por favor, ingresa un correo electrónico válido.');
      return;
    }

    // 🔹 Verificar que las contraseñas coincidan
    if (contrasena !== confirmarContrasena) {
      setError('Las contraseñas no coinciden');
      return;
    }

    // Si todas las validaciones pasan, activamos el modo de carga
    setLoading(true);

    try {
      // 🔸 Enviamos la solicitud de registro al backend
      const response = await safeFetch(`/api/registrar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // Indicamos que enviamos JSON
        },
        body: JSON.stringify({
          email,                    // Correo del usuario
          nombre_usuario: usuario,  // Nombre de usuario
          contrasena_hash: contrasena, // Contraseña (el backend la encriptará)
        }),
      });

      // Convertimos la respuesta a JSON
      const data = await response.json();

      // 🔻 Si el backend responde con un error (status no 200)
      if (!response.ok) {
        const errorMessage = data?.message || 'El usuario ya existe, prueba con otro.';
        setError(errorMessage);
        return;
      }

      // 🔹 Si todo sale bien, mostramos mensaje de verificación
      setMensaje(`Verifica tu correo`);

      // Guardamos el email en localStorage (puede usarse en el flujo de verificación)
      localStorage.setItem('registroEmail', email);

      // 🔸 Redirección automática tras unos segundos
      setTimeout(() => {
        setMensaje('');
        if (onSuccess) onSuccess(); // Llamamos a la función de éxito si fue pasada por props
        navigate('/verificacionEnviada'); // Redirige a una página de confirmación
      }, 5000);

    } catch (err) {
      // 🔻 Manejamos errores de red o conexión
      console.error('Error en el registro:', err);
      setError('Error de conexión. Inténtalo de nuevo más tarde.');
    } finally {
      // 🔹 Siempre desactiva el modo de carga, aunque haya error o éxito
      setLoading(false);
    }
  };

  // 🧱 Renderizado del formulario
  return (
    <form className="space-y-2 flex flex-col justify-between h-full" onSubmit={handleSubmit}>

      {/* Campo de nombre de usuario */}
      <div className="relative border-b border-white focus-within:border-white">
        <input
          type="text"
          id="usuario"
          placeholder="Nombre Usuario"
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          required
          className="w-full text-white bg-black outline-none py-2 placeholder-white focus:placeholder-transparent"
        />
      </div>

      {/* Campo de correo electrónico */}
      <div className="relative border-b border-white focus-within:border-white">
        <input
          type="email"
          id="email"
          placeholder="Correo Electrónico"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full text-white bg-black outline-none py-2 placeholder-white focus:placeholder-transparent"
        />
      </div>

      {/* Campo de contraseña */}
      <div className="relative border-b border-white focus-within:border-white my-4">
        <input
          type={showPassword ? "text" : "password"}
          id="contrasena"
          placeholder="Contraseña"
          value={contrasena}
          onChange={(e) => setContrasena(e.target.value)}
          required
          className="w-full text-white bg-black outline-none py-2 placeholder-white focus:placeholder-transparent pl-2 rounded-md"
          autoComplete="current-password"
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-700 bg-transparent border-none p-0"
        >
          {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      </div>

      {/* Campo de confirmación de contraseña */}
      <div className="relative border-b border-white focus-within:border-white">
        <input
          type={showPassword ? "text" : "password"}
          id="confirmar_contrasena"
          placeholder="Confirmar Contraseña"
          value={confirmarContrasena}
          onChange={(e) => setConfirmarContrasena(e.target.value)}
          required
          className="w-full text-white bg-black outline-none py-2 placeholder-white focus:placeholder-transparent pl-2 rounded-md"
          
        />

        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-700  bg-transparent border-none p-0"
        >
          {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>

      </div>

      {/* Mensajes de error o éxito */}
      {mensaje && !error && <p className="text-green-500 text-sm">{mensaje}</p>}
      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Botón de registro */}
      <ButtonSiguiente
        type="submit"
        className="w-full mt-4 font-bold hover:bg-gray-900 p-3"
        disabled={loading}
      >
        {loading ? 'Enviando...' : 'Registrarse'}
      </ButtonSiguiente>
    </form>
  );
};

// Exportamos el componente
export default RegisterForm;
